/**
 * In-page translators — Chrome's built-in Google Translate, and extensions that
 * work the same way — rewrite the DOM underneath React: text nodes get wrapped in
 * <font> elements and reparented. React keeps referencing the original nodes, so
 * the next unmount calls removeChild/insertBefore with a node that no longer
 * belongs to the parent React remembers. The browser throws NotFoundError, React
 * treats it as a commit-phase error and tears down the entire tree, and the user
 * lands on the "app failed to render" screen.
 *
 * See facebook/react#11538 — still unfixed in React 19.
 *
 * index.html opts out of translation, which prevents the common case, but a user
 * can still force a translation manually. This guard makes the two mutations
 * React relies on non-fatal so a translated page degrades instead of dying.
 */
import { captureTranslationDomMismatch } from "@/lib/sentry";

type GuardedOperation = "removeChild" | "insertBefore";

let installed = false;
let reported = false;

function documentLooksTranslated(): boolean {
  const { classList } = document.documentElement;
  return classList.contains("translated-ltr") || classList.contains("translated-rtl");
}

/**
 * Reports at most once per page load: once the DOM is out of sync the mismatch
 * tends to repeat on every render, and we only need to know that it happened.
 */
function reportMismatch(operation: GuardedOperation): void {
  if (reported) return;
  reported = true;

  const translated = documentLooksTranslated();
  console.warn(
    `[translation-dom-guard] Se ignoró un ${operation} sobre un nodo que ya no pertenece a su padre` +
      (translated ? " (traductor del navegador activo)." : "."),
  );
  captureTranslationDomMismatch(operation, translated);
}

export function installTranslationDomGuard(): void {
  if (installed || typeof Node === "undefined") return;
  installed = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function guardedRemoveChild<T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      reportMismatch("removeChild");
      // React only needs the node gone from its own bookkeeping; returning it
      // unremoved matches what removeChild would have handed back on success.
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  const originalAppendChild = Node.prototype.appendChild;
  Node.prototype.insertBefore = function guardedInsertBefore<T extends Node>(
    this: Node,
    node: T,
    child: Node | null,
  ): T {
    if (child != null && child.parentNode !== this) {
      reportMismatch("insertBefore");
      // Append rather than drop: React has already recorded this placement as
      // committed and will never retry it, so returning without attaching the
      // node anywhere would silently erase real UI. Landing it at the end of the
      // intended parent can misorder siblings on a translated page — a far
      // cheaper failure than a missing element.
      return originalAppendChild.call(this, node) as T;
    }
    return originalInsertBefore.call(this, node, child) as T;
  };
}
